import { useEffect, useState, type FormEvent } from 'react';
import type { CreateTicketStatusInput, TicketStatus } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { CheckboxField, TextField } from '../../components/ui/Field.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { Modal } from '../../components/ui/Modal.js';
import {
  useCreateTicketStatus,
  useDeleteTicketStatus,
  useUpdateTicketStatus,
} from './api.js';

interface TicketStatusFormModalProps {
  isOpen: boolean;
  status: TicketStatus | null;
  onClose: () => void;
}

interface FormState {
  name: string;
  sortOrder: string;
  isDefault: boolean;
  isClosed: boolean;
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  sortOrder: '0',
  isDefault: false,
  isClosed: false,
  isActive: true,
};

function toFormState(status: TicketStatus | null): FormState {
  if (!status) {
    return EMPTY_FORM;
  }
  return {
    name: status.name,
    sortOrder: String(status.sortOrder),
    isDefault: status.isDefault,
    isClosed: status.isClosed,
    isActive: status.isActive,
  };
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

export function TicketStatusFormModal({
  isOpen,
  status: statusProp,
  onClose,
}: TicketStatusFormModalProps): JSX.Element {
  const [status, setStatus] = useState<TicketStatus | null>(statusProp);
  const isEdit = status !== null;
  const saveMode = useSaveMode();
  const [form, setForm] = useState<FormState>(() => toFormState(status));
  const [confirmOpen, setConfirmOpen] = useState(false);

  const createMutation = useCreateTicketStatus();
  const updateMutation = useUpdateTicketStatus();
  const deleteMutation = useDeleteTicketStatus();
  const mutation = isEdit ? updateMutation : createMutation;

  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const resetSaveMode = saveMode.reset;

  useEffect(() => {
    if (isOpen) {
      setStatus(statusProp);
    }
  }, [isOpen, statusProp]);

  useEffect(() => {
    if (isOpen) {
      setForm(toFormState(status));
      setConfirmOpen(false);
      resetSaveMode();
      resetCreate();
      resetUpdate();
    }
  }, [isOpen, status, resetSaveMode, resetCreate, resetUpdate]);

  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError =
    mutation.error instanceof ApiError && !mutation.error.envelope?.error.details?.length
      ? mutation.error.message
      : mutation.isError && !(mutation.error instanceof ApiError)
        ? 'Failed to save ticket status.'
        : null;

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const payload: CreateTicketStatusInput = {
      name: form.name.trim(),
      sortOrder: Number.parseInt(form.sortOrder, 10) || 0,
      isDefault: form.isDefault,
      isClosed: form.isClosed,
      isActive: form.isActive,
    };
    if (isEdit) {
      updateMutation.mutate(
        { id: status.id, input: payload },
        { onSuccess: () => saveMode.closeAfterSaveRef.current && onClose() },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: (created) => {
          if (saveMode.closeAfterSaveRef.current) {
            onClose();
          } else {
            setStatus(created);
          }
        },
      });
    }
  };

  const handleDelete = (): void => {
    if (!status) {
      return;
    }
    deleteMutation.mutate(status.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        onClose();
      },
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? 'Edit ticket status' : 'New ticket status'}
        footer={
          <FormActions
            formId="ticket-status-form"
            saveMode={saveMode}
            isPending={mutation.isPending}
            onCancel={onClose}
            leading={
              isEdit ? (
                <Button
                  variant="ghost"
                  className="mr-auto text-rose-600 hover:bg-rose-50"
                  onClick={() => setConfirmOpen(true)}
                  disabled={mutation.isPending || deleteMutation.isPending}
                >
                  Delete
                </Button>
              ) : null
            }
          />
        }
      >
        <form id="ticket-status-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
          {generalError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {generalError}
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-4">
            <TextField
              label="Name"
              required
              value={form.name}
              maxLength={100}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              error={fieldErrors.name}
            />
            <TextField
              label="Sort order"
              type="number"
              value={form.sortOrder}
              onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
              error={fieldErrors.sortOrder}
            />
          </div>

          <CheckboxField
            label="Default status for new tickets"
            checked={form.isDefault}
            onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))}
          />
          <CheckboxField
            label="Closed/terminal status"
            checked={form.isClosed}
            onChange={(e) => setForm((f) => ({ ...f, isClosed: e.target.checked }))}
          />
          <CheckboxField
            label="Active"
            checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
          />
        </form>
      </Modal>
      <ConfirmDialog
        isOpen={confirmOpen}
        title="Delete ticket status"
        message={`Delete "${status?.name ?? ''}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
